import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  canonical?: string;
  noindex?: boolean;
}

const defaultSEO = {
  title: 'Quick Free Invoice Tool — Free Online Invoice Generator & Quotation Maker',
  description: 'Quick Free Invoice Tool is a fast, 100% free online invoice generator. Create & download professional PDF & Word invoices in 60s. Supports 170+ currencies, VAT/GST taxes, and instant estimates.',
  keywords: 'quick free invoice tool, free invoice generator, online invoice maker, free quotation generator, estimate maker, pdf invoice creator, freelancer billing software, small business invoicing, contractor invoice template, instant invoice generator no sign up, vat gst invoice maker',
  ogImage: 'https://quickfreeinvoicetool.com/og-image.png',
};

export function SEO({ title, description, keywords, ogImage, canonical, noindex }: SEOProps) {
  const location = useLocation();

  useEffect(() => {
    // Update document title
    document.title = title || defaultSEO.title;

    // Update meta tags
    updateMetaTag('name', 'description', description || defaultSEO.description);
    updateMetaTag('name', 'keywords', keywords || defaultSEO.keywords);
    
    // Update robots tag
    if (noindex) {
      updateMetaTag('name', 'robots', 'noindex, nofollow');
    } else {
      updateMetaTag('name', 'robots', 'index, follow');
    }
    
    // Update OG tags
    updateMetaTag('property', 'og:title', title || defaultSEO.title);
    updateMetaTag('property', 'og:description', description || defaultSEO.description);
    updateMetaTag('property', 'og:image', ogImage || defaultSEO.ogImage);
    updateMetaTag('property', 'og:url', `https://quickfreeinvoicetool.com${location.pathname}`);
    
    // Update Twitter tags
    updateMetaTag('name', 'twitter:card', 'summary_large_image');
    updateMetaTag('name', 'twitter:title', title || defaultSEO.title);
    updateMetaTag('name', 'twitter:description', description || defaultSEO.description);
    updateMetaTag('name', 'twitter:image', ogImage || defaultSEO.ogImage);
    
    // Update canonical link
    updateCanonicalLink(canonical || `https://quickfreeinvoicetool.com${location.pathname}`);
  }, [title, description, keywords, ogImage, canonical, noindex, location.pathname]);

  const pathSegments = location.pathname.split('/').filter(Boolean);
  
  if (pathSegments.length === 0) {
    return null;
  }

  const breadcrumbList = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      {
        '@type': 'ListItem',
        'position': 1,
        'name': 'Home',
        'item': 'https://quickfreeinvoicetool.com'
      },
      ...pathSegments.map((segment, index) => {
        const path = '/' + pathSegments.slice(0, index + 1).join('/');
        const name = segment
          .replace(/[-_]+/g, ' ')
          .replace(/\b\w/g, char => char.toUpperCase());
        return {
          '@type': 'ListItem',
          'position': index + 2,
          'name': name,
          'item': `https://quickfreeinvoicetool.com${path}`
        };
      })
    ]
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbList) }}
    />
  );
}

function updateMetaTag(attribute: string, attributeValue: string, content: string) {
  let element = document.querySelector(`meta[${attribute}="${attributeValue}"]`);
  
  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, attributeValue);
    document.head.appendChild(element);
  }
  
  element.setAttribute('content', content);
}

function updateCanonicalLink(url: string) {
  let link = document.querySelector('link[rel="canonical"]');
  
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  
  link.setAttribute('href', url);
}
